import { Button, Spacer, TextInput, Typography } from "@fullscript/aviary-web";
import { faChevronRight, faCircleInfo } from "@fullscript/pro-regular-svg-icons";
import { useState } from "react";

import { EmailInput } from "@shared/components/EmailInput/EmailInput";
import { isEmailValid } from "@shared/components/EmailInput/EmailValidation/EmailValidation";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import type { SelfCheckoutEntryField } from "@shared/selfCheckoutFlow/selfCheckoutFlow";

import * as styles from "./AvailabilityGate.styles";

interface AvailabilityGateValues {
  email: string;
  zipCode?: string;
}

interface AvailabilityGateCopy {
  title: string;
  description?: string;
  emailLabel: string;
  /** Required (not optional) so the zip field can never render unlabelled. */
  zipCodeLabel: string;
  zipInvalidError: string;
  locationNote?: string;
  continueLabel: string;
  emailInvalidError: string;
}

interface Props {
  /** Fields to render, from the flow descriptor's entryFields. */
  fields: SelfCheckoutEntryField[];
  copy: AvailabilityGateCopy;
  onContinue: (values: AvailabilityGateValues) => void;
  loading?: boolean;
  initialEmail?: string;
  initialZipCode?: string;
  /** Store-configured email regex, when the surface provides one. */
  emailRegex?: RegExp;
}

const US_ZIP_INPUT_PATTERN = /^\s*\d{5}(-\d{4})?\s*$/;

/**
 * Presentational availability/entry form shared by the self-checkout flows.
 *
 * Renders the fields the flow descriptor declares (email, and zip when the
 * flow gates on location) with copy supplied by the surface. No flow
 * detection, no locales, no network — submitting simply calls `onContinue`
 * with the collected values; the surface decides what check to run (Journey
 * zip serviceability vs direct-lab state-restriction eligibility).
 */
const AvailabilityGate = ({
  fields,
  copy,
  onContinue,
  loading = false,
  initialEmail = "",
  initialZipCode = "",
  emailRegex = new RegExp(""),
}: Props) => {
  const [email, setEmail] = useState(initialEmail);
  const [isEmailDirty, setIsEmailDirty] = useState(false);
  const [zipCode, setZipCode] = useState(initialZipCode);
  const [isZipDirty, setIsZipDirty] = useState(false);

  const showZip = fields.includes("zip");
  const isValidEmail = isEmailValid(email, emailRegex);
  const isValidZip = !showZip || US_ZIP_INPUT_PATTERN.test(zipCode);
  const canSubmit = isValidEmail && isValidZip && !loading;

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setIsEmailDirty(true);
  };

  const handleEmailSet = (value: string) => {
    setEmail(value);
    setIsEmailDirty(true);
  };

  const handleZipCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setZipCode(event.target.value);
    setIsZipDirty(true);
  };

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();

    if (!canSubmit) {
      setIsEmailDirty(true);
      setIsZipDirty(true);

      return;
    }

    onContinue({ email, zipCode: showZip ? zipCode.trim() : undefined });
  };

  const emailError = isEmailDirty && !isValidEmail ? copy.emailInvalidError : undefined;
  const zipError = isZipDirty && !isValidZip ? copy.zipInvalidError : undefined;

  return (
    <div>
      <Typography variant="h2" intention="neutral" colorIntensity="bold">
        {copy.title}
      </Typography>
      <Spacer size="large" />
      {!!copy.description && (
        <>
          <Typography>{copy.description}</Typography>
          <Spacer size="medium" />
        </>
      )}
      <form css={styles.form} onSubmit={handleSubmit}>
        <EmailInput
          label={copy.emailLabel}
          value={email}
          setEmail={handleEmailSet}
          onChange={handleEmailChange}
          isDirty={isEmailDirty}
          isLoading={loading}
          required
          error={emailError}
        />
        {!!showZip && (
          <>
            <Spacer size="medium" />
            <TextInput
              id="availabilityGateZipCode"
              label={copy.zipCodeLabel}
              value={zipCode}
              onChange={handleZipCodeChange}
              inputMode="numeric"
              autoComplete="postal-code"
              errorCaption={zipError}
              aria-invalid={zipError ? true : undefined}
              required
            />
            {!!copy.locationNote && (
              <>
                <Spacer size="xsmall" />
                <div css={styles.locationNote}>
                  <FontAwesomeIcon icon={faCircleInfo} size="sm" />
                  <Typography variant="footnote" intention="neutral" colorIntensity="subtle">
                    {copy.locationNote}
                  </Typography>
                </div>
              </>
            )}
          </>
        )}
        <Spacer size="large" />
        <Button
          type="submit"
          variant="primary"
          isFullWidth
          size="large"
          isPending={loading}
          disabled={!canSubmit}
          rightIcon={faChevronRight}
        >
          {copy.continueLabel}
        </Button>
      </form>
    </div>
  );
};

export { AvailabilityGate };
export type { AvailabilityGateCopy, AvailabilityGateValues };
